/**
 * filtro.js — Busca e Filtros do Estoque.
 *
 * Responsabilidades:
 *  - Popular o <select> de marcas via Edge Function `public-marcas`.
 *  - Ouvir mudanças nos filtros (texto, marca, câmbio, combustível, ordenação).
 *  - Aplicar o filtro client-side sobre `_todosVeiculos` do estoque.
 *  - Gerenciar o estado "filtro ativo" e o botão "Limpar".
 *  - Conectar o botão "Buscar" da hero ao scroll + execução do filtro.
 */

import { fetchMarcas }                      from '../services/marcas.js';
import { _todosVeiculos, aplicarFiltro }    from './estoque.js';

// ── Estado interno ─────────────────────────────────────────────
let _filtros = {
  texto:      '',
  marca:      '',
  cambio:     '',
  combustivel: '',
  precoMin:   '',
  precoMax:   '',
  ordem:      '',   // 'preco-asc' | 'preco-desc' | 'ano-desc'
};

// ── Helpers DOM ────────────────────────────────────────────────
const $ = id => document.getElementById(id);

// ── Popular select de marcas ───────────────────────────────────

async function popularMarcas() {
  const sel = $('filter-marca');
  if (!sel) return;

  const { data: marcas, error } = await fetchMarcas();
  if (error || !marcas || !marcas.length) return;

  // Remove opções hardcoded (mantém apenas o "Todas as Marcas")
  sel.innerHTML = '<option value="">Todas as Marcas</option>';

  marcas.forEach(m => {
    const opt = document.createElement('option');
    opt.value = m.nome.toLowerCase();
    opt.textContent = m.nome;
    sel.appendChild(opt);
  });
}

// ── Popular select de combustíveis ─────────────────────────────

function popularCombustiveis() {
  const sel = $('filter-combustivel');
  if (!sel) return;

  // Extrai combustíveis únicos dos veículos carregados
  const combustiveis = [...new Set(_todosVeiculos.map(v => v.combustivel).filter(Boolean))];
  
  // Ordena alfabeticamente
  combustiveis.sort((a, b) => a.localeCompare(b, 'pt-BR'));

  // Define a opção padrão
  sel.innerHTML = '<option value="">Todos</option>';

  combustiveis.forEach(c => {
    const opt = document.createElement('option');
    opt.value = c.toLowerCase();
    opt.textContent = c;
    sel.appendChild(opt);
  });
}

// ── Lógica de filtro ───────────────────────────────────────────

function normalizar(str) {
  return (str ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function filtrarEOrdenar() {
  const { texto, marca, cambio, combustivel, precoMin, precoMax, ordem } = _filtros;
  const q        = normalizar(texto);
  const minVal   = precoMin !== '' ? Number(precoMin) : null;
  const maxVal   = precoMax !== '' ? Number(precoMax) : null;

  let resultado = _todosVeiculos.filter(v => {
    // Texto livre: nome + modelo + marca
    if (q) {
      const haystack = normalizar(`${v.name} ${v.marcaNome} ${v.cambio} ${v.combustivel}`);
      if (!haystack.includes(q)) return false;
    }

    // Marca exata (valor já em lowercase)
    if (marca && v.marca !== marca) return false;

    // Câmbio
    if (cambio && normalizar(v.cambio) !== normalizar(cambio)) return false;

    // Combustível
    if (combustivel && normalizar(v.combustivel) !== normalizar(combustivel)) return false;

    // Faixa de preço
    if (minVal !== null && v.priceRaw < minVal) return false;
    if (maxVal !== null && v.priceRaw > maxVal) return false;

    return true;
  });

  // Ordenação
  if (ordem === 'preco-asc')  resultado.sort((a, b) => a.priceRaw - b.priceRaw);
  if (ordem === 'preco-desc') resultado.sort((a, b) => b.priceRaw - a.priceRaw);
  if (ordem === 'ano-desc')   resultado.sort((a, b) => b.yearNum  - a.yearNum);

  return resultado;
}

function executarFiltro() {
  const lista = filtrarEOrdenar();
  aplicarFiltro(lista);
  atualizarBotaoLimpar();

  // Evento: "limpar" no estado vazio
  const btnVazio = $('btn-limpar-filtro-vazio');
  if (btnVazio) btnVazio.addEventListener('click', limparFiltros);
}

// ── Botão Limpar ───────────────────────────────────────────────

function atualizarBotaoLimpar() {
  const btn = $('btn-limpar-filtros');
  if (!btn) return;

  const ativo = Object.values(_filtros).some(v => v !== '');
  btn.classList.toggle('filtro-ativo', ativo);
}

function limparFiltros() {
  _filtros = { texto: '', marca: '', cambio: '', combustivel: '', precoMin: '', precoMax: '', ordem: '' };

  const input = $('search-input');
  if (input) input.value = '';

  ['filter-marca', 'filter-cambio', 'filter-combustivel', 'filter-ordem'].forEach(id => {
    const el = $(id);
    if (el) el.value = '';
  });

  const precoMin = $('filter-preco-min');
  const precoMax = $('filter-preco-max');
  if (precoMin) precoMin.value = '';
  if (precoMax) precoMax.value = '';

  const resumo = $('preco-resumo');
  if (resumo) resumo.textContent = '';

  executarFiltro();
}

// ── Listeners dos controles ────────────────────────────────────

function bindControls() {
  // Input de texto — debounce 300ms
  let debounceTimer;
  const searchInput = $('search-input');
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        _filtros.texto = searchInput.value.trim();
        executarFiltro();
      }, 300);
    });
  }

  // Select de marca
  const selMarca = $('filter-marca');
  if (selMarca) {
    selMarca.addEventListener('change', () => {
      _filtros.marca = selMarca.value;
      executarFiltro();
    });
  }

  // Select de câmbio
  const selCambio = $('filter-cambio');
  if (selCambio) {
    selCambio.addEventListener('change', () => {
      _filtros.cambio = selCambio.value;
      executarFiltro();
    });
  }

  // Select de combustível
  const selCombustivel = $('filter-combustivel');
  if (selCombustivel) {
    selCombustivel.addEventListener('change', () => {
      _filtros.combustivel = selCombustivel.value;
      executarFiltro();
    });
  }

  // Select de ordenação
  const selOrdem = $('filter-ordem');
  if (selOrdem) {
    selOrdem.addEventListener('change', () => {
      _filtros.ordem = selOrdem.value;
      executarFiltro();
    });
  }

  // Inputs de faixa de preço — debounce 500ms
  let precoTimer;
  const inputPrecoMin = $('filter-preco-min');
  const inputPrecoMax = $('filter-preco-max');

  function onPrecoChange() {
    clearTimeout(precoTimer);
    precoTimer = setTimeout(() => {
      _filtros.precoMin = inputPrecoMin?.value ?? '';
      _filtros.precoMax = inputPrecoMax?.value ?? '';

      // Atualiza o resumo visual
      const resumo = $('preco-resumo');
      if (resumo) {
        const min = _filtros.precoMin ? `R$ ${Number(_filtros.precoMin).toLocaleString('pt-BR')}` : '';
        const max = _filtros.precoMax ? `R$ ${Number(_filtros.precoMax).toLocaleString('pt-BR')}` : '';
        if (min && max) resumo.textContent = `${min} até ${max}`;
        else if (min)   resumo.textContent = `A partir de ${min}`;
        else if (max)   resumo.textContent = `Até ${max}`;
        else            resumo.textContent = '';
      }

      executarFiltro();
    }, 500);
  }

  if (inputPrecoMin) inputPrecoMin.addEventListener('input', onPrecoChange);
  if (inputPrecoMax) inputPrecoMax.addEventListener('input', onPrecoChange);

  // Botão "Buscar" — rola até o estoque e aplica
  const btnBuscar = $('btn-buscar');
  if (btnBuscar) {
    btnBuscar.addEventListener('click', (e) => {
      e.preventDefault();
      // Atualiza filtro de texto com o valor atual do input
      if (searchInput) _filtros.texto = searchInput.value.trim();
      executarFiltro();
      // Scroll até o estoque
      const secEstoque = $('estoque');
      if (secEstoque) secEstoque.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // Botão "Limpar filtros"
  const btnLimpar = $('btn-limpar-filtros');
  if (btnLimpar) {
    btnLimpar.addEventListener('click', limparFiltros);
  }

  // Enter no campo de busca
  if (searchInput) {
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        _filtros.texto = searchInput.value.trim();
        executarFiltro();
        const secEstoque = $('estoque');
        if (secEstoque) secEstoque.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }
}

// ── Inicialização ──────────────────────────────────────────────

/**
 * Inicializa o sistema de filtros.
 * Deve ser chamado APÓS renderEstoque() ter populado `_todosVeiculos`.
 */
export async function initFiltro() {
  await popularMarcas();
  popularCombustiveis();
  bindControls();
  atualizarBotaoLimpar();
}
